import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  QUERY_GET_PORTAL_NOTES,
  useCreatePortalNote,
  useDeletePortalNote,
  useGetPortalNote,
  useUpdatePortalNote
} from '@apps-common/apis/portal/portal-note'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements/button'
import { ImageSelect, TextAreaSM } from '@apps-common/components/elements/form'
import { ConfirmModalAction } from '@apps-common/components/modals'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer,
  ModalTopConfirmActionButton
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { queryClient } from '@apps-common/lib'
import { IPortalNoteModel } from '@apps-common/types/portal/portal-note'

export interface IPortalNoteModalProps {
  entityId: string
  noteId?: number
}

export const PortalNoteModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IPortalNoteModalProps
}) => {
  const { entityId, noteId } = componentProps
  const isEditing = noteId !== undefined
  const [image, setImage] = useState<any>(null)

  const {
    data: note,
    isLoading: noteIsLoading,
    isError: noteIsError
  } = useGetPortalNote({
    config: {
      enabled: noteId !== undefined
    },
    noteId: noteId || 0
  })

  const refetchQueries = (role: string) => {
    queryClient.invalidateQueries({ queryKey: QUERY_GET_PORTAL_NOTES() })
    onDismiss(null, role)
  }

  const { mutate: create, isPending: createIsPending } = useCreatePortalNote(true, () => refetchQueries(ModalDismiss.Confirm))
  const { mutate: update, isPending: updateIsPending } = useUpdatePortalNote(true, () => refetchQueries(ModalDismiss.Confirm))
  const { mutate: updatePin, isPending: updatePinIsPending } = useUpdatePortalNote(true, () => refetchQueries(ModalDismiss.Confirm))
  const { mutate: deleteNote, isPending: deleteIsPending } = useDeletePortalNote(true, () => refetchQueries(ModalDismiss.Confirm))

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue
  } = useForm<IPortalNoteModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      portalNoteId: noteId,
      entityId,
      noteContent: '',
      noteImage1: '',
      noteImage2: '',
      noteImage3: '',
      dateEdited: new Date().toISOString(),
      userId: 0,
      userName: ''
    }
  })

  useEffect(() => {
    if (note) {
      reset(note)
    }
  }, [note, reset])

  const submit = (values: IPortalNoteModel) => {
    if (noteId) {
      update({ model: values, image })
    } else {
      create({ model: values, image })
    }
  }

  const noteImage = watch('noteImage1')

  // const ImageIcon = commonIcons.image
  // const AddIcon = commonIcons.add
  // const ClearIcon = commonIcons.closeCircle

  const isLoading = noteIsLoading || noteIsError
  const isUpdating = createIsPending || updateIsPending || updatePinIsPending || deleteIsPending

  return (
    <ModalContainer innerClasses='sm:max-w-[400px] !min-h-0 !h-auto md:!min-h-[50vh]'>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle={isEditing ? 'Update' : 'Create'} title='Note' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>

      <ModalBody classes='!pt-[100px] !overflow-auto'>
        {isEditing && (
          <div className='flex items-center justify-between mb-[20px] gap-[10px]'>
            <ModalTopConfirmActionButton
              loading={isLoading || isUpdating}
              confirmProps={
                note?.pinned
                  ? {
                      text: 'Un-pin note?',
                      description: 'This note will be un-pinned from the device management page.',
                      action: ConfirmModalAction.Warning
                    }
                  : {
                      text: 'Pin note?',
                      description:
                        'This note will be pinned to the device management page. This will replace any other pinned note on the device.',
                      action: ConfirmModalAction.Success
                    }
              }
              confirmCallback={() => updatePin({ model: { ...note, pinned: !note?.pinned } as any })}
              text={!note?.pinned ? 'Pin to device' : 'Un-pin from device'}
              reverseIcon
              icon='pin'
              classes='action-info'
            />
            <ModalTopConfirmActionButton
              loading={isLoading || isUpdating}
              confirmProps={{
                text: 'Delete note?',
                description: 'Are you sure you would like to delete this note?',
                action: ConfirmModalAction.Error
              }}
              confirmCallback={() => deleteNote({ id: note?.portalNoteId || 0 })}
              text='Delete'
              icon='delete'
              classes='action-error'
            />
          </div>
        )}
        <form noValidate id='unit-portal-note-modify-form' onSubmit={handleSubmit(submit)}>
          <TextAreaSM
            key='portal-note-content'
            type='text'
            placeholder='Enter note contents'
            label='Note contents'
            id='portal-note-content'
            loading={isLoading}
            disabled={isUpdating}
            className='min-h-[180px]'
            registration={register('noteContent', {
              required: true,
              maxLength: {
                value: 300,
                message: 'No longer than 300 characters '
              }
            })}
            errors={errors.noteContent}
          />
          <div className=''>
            <ImageSelect
              key='note-image-input'
              existingImageUrl={noteImage}
              outerClasses='overflow-hidden rounded-3xl bg-surface border border-border text-text'
              containerClasses='w-full pt-[60%] rounded-[10px] overflow-hidden'
              selectedImageClasses='absolute top-0 left-0 min-w-full min-h-full bg-surface bg-center rounded-[10px]'
              clearClasses='absolute top-[5px] right-[5px] z-[2]'
              existingClasses='absolute top-0 left-0 min-w-full min-h-full bg-surface bg-center rounded-[10px]'
              cropDimensions={{ width: 600, height: 400 }}
              clearChildren={
                <div className='action-info p-[1px] w-[25px] h-[25px] rounded-[50%] flex items-center justify-center'>
                  <commonIcons.close className='text-[1.3rem]' />
                </div>
              }
              onChangeCallback={(data) => {
                if (data) {
                  setImage(data)
                  setValue('noteImage1', '')
                }
              }}
              onClearCallback={() => {
                setImage(null)
                setValue('noteImage1', '')
              }}
            >
              <div className='absolute top-0 left-0 w-full h-full rounded-[10px] gap-[10px] flex items-center justify-center'>
                <div className='bg-surface-2 px-4 py-3 rounded-2xl flex flex-col items-center justify-center gap-[5px] border border-border'>
                  <ButtonRoundSm type='div' className='bg-surface'>
                    <commonIcons.add />
                  </ButtonRoundSm>
                  <div className='jost-light text-[0.8em]'>Add image</div>
                </div>
              </div>
            </ImageSelect>
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-[10px]'>
          <ButtonRoundedSm
            type='submit'
            form='unit-portal-note-modify-form'
            className='action-success'
            loading={isLoading || isUpdating}
            text={isEditing ? 'Update' : 'Create'}
          />
          <ButtonRoundedSm
            type='button'
            loading={isLoading || isUpdating}
            className='action-warning'
            text='Cancel'
            callback={() => onDismiss(null, ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
